import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Hammer } from "lucide-react";
import { services, companyInfo } from "../data";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Resize listener til at styre burgermenuen præcist under 1200px
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Luk dropdown når man klikker udenfor
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Luk menuer ved ruteskift
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  const isMobile = windowWidth < 1200;

  const isActive = (path) => location.pathname === path;
  const isServiceActive = (serviceId) => location.pathname === `/ydelser/${serviceId}`;
  const isAnyServiceActive = services.some(s => isServiceActive(s.id));

  return (
    <header className="sticky top-0 z-50 w-full border-b border-neutral-200 bg-white text-neutral-900 shadow-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-5 sm:px-6 lg:px-8 sm:py-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/height=50,quality=95,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/hellerup-logo-trans.png" 
            alt="Henneberg Byggeri Logo" 
            className="h-10 w-auto object-contain sm:h-12"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        {!isMobile && (
          <nav className="flex items-center gap-8 text-sm font-bold uppercase tracking-wider">
            <Link
              to="/"
              className={`transition-colors duration-200 hover:text-black ${
                isActive("/") ? "text-black" : "text-neutral-500"
              }`}
            >
              Forside
            </Link>

            {/* Ydelser Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                onMouseEnter={() => setIsDropdownOpen(true)}
                className={`flex items-center gap-1 transition-colors duration-200 hover:text-black focus:outline-none uppercase ${
                  isAnyServiceActive ? "text-black" : "text-neutral-500"
                }`}
              >
                Ydelser
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {isDropdownOpen && (
                <div
                  className="absolute left-0 mt-2 w-64 rounded-md border border-neutral-200 bg-white py-2 shadow-xl"
                  onMouseLeave={() => setIsDropdownOpen(false)}
                >
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/ydelser/${service.id}`}
                      className={`block px-4 py-2.5 text-[13px] font-bold uppercase tracking-wider transition-colors hover:bg-neutral-50 hover:text-black ${
                        isServiceActive(service.id) ? "bg-neutral-50 text-black" : "text-neutral-500"
                      }`}
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/galleri"
              className={`transition-colors duration-200 hover:text-black ${
                isActive("/galleri") ? "text-black" : "text-neutral-500"
              }`}
            >
              Galleri
            </Link>

            <Link
              to="/om-os"
              className={`transition-colors duration-200 hover:text-black ${
                isActive("/om-os") ? "text-black" : "text-neutral-500"
              }`}
            >
              Om Os
            </Link>

            <Link
              to="/kontakt"
              className={`transition-colors duration-200 hover:text-black ${
                isActive("/kontakt") ? "text-black" : "text-neutral-500"
              }`}
            >
              Kontakt
            </Link>

            {/* CTA Button */}
            <Link
              to="/kontakt"
              className="ml-4 rounded-none bg-[#434359] px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-[#343446] hover:shadow-lg active:scale-95"
            >
              Få et tilbud
            </Link>
          </nav>
        )}

        {/* Burgermenu Knap (< 1200px) */}
        {isMobile && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="rounded p-2 text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900 focus:outline-none"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        )}
      </div>

      {/* Mobil Navigation Panel (< 1200px) */}
      {isMobile && isOpen && (
        <div className="border-t border-neutral-200 bg-white px-4 py-4 shadow-inner">
          <nav className="flex flex-col gap-4">
            <Link
              to="/"
              className={`block py-2 text-base font-bold uppercase tracking-wider transition-colors hover:text-black ${
                isActive("/") ? "text-black" : "text-neutral-500"
              }`}
            >
              Forside
            </Link>

            {/* Ydelser sektion i mobilmenu */}
            <div className="border-l-2 border-neutral-200 pl-4 my-1">
              <span className="block py-1 text-xs font-bold uppercase tracking-widest text-[#434359]">
                Vores Ydelser
              </span>
              <div className="mt-2 flex flex-col gap-2.5">
                {services.map((service) => (
                  <Link
                    key={service.id}
                    to={`/ydelser/${service.id}`}
                    className={`block py-1 text-sm font-bold uppercase tracking-wider transition-colors hover:text-black ${
                      isServiceActive(service.id) ? "text-black" : "text-neutral-500"
                    }`}
                  >
                    {service.title}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/galleri"
              className={`block py-2 text-base font-bold uppercase tracking-wider transition-colors hover:text-black ${
                isActive("/galleri") ? "text-black" : "text-neutral-500"
              }`}
            >
              Galleri
            </Link>

            <Link
              to="/om-os"
              className={`block py-2 text-base font-bold uppercase tracking-wider transition-colors hover:text-black ${
                isActive("/om-os") ? "text-black" : "text-neutral-500"
              }`}
            >
              Om Os
            </Link>

            <Link
              to="/kontakt"
              className={`block py-2 text-base font-bold uppercase tracking-wider transition-colors hover:text-black ${
                isActive("/kontakt") ? "text-black" : "text-neutral-500"
              }`}
            >
              Kontakt
            </Link>

            <Link
              to="/kontakt"
              className="mt-2 w-full rounded-none bg-[#434359] py-3 text-center text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#343446]"
            >
              Få et tilbud
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
